'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import UIButton from '@/components/atoms/UIButtons/UIButton';
import { IconArrow } from '@/components/atoms/Icons';
import leftBg from 'assets/images/left-bg.png';
import styles from './HeartHunter.module.css';
import HeartHunterCard from './HeartHunterCard';
export default function HeartHunters() {
  const [items, setItems] = useState([]);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [maxWidth, setMaxWidth] = useState('100%');

  /* ---------------- EMBLA ---------------- */
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: false,
    dragFree: false
  });
  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
    setHasScrolled(true);
  }, [emblaApi]);

  /* -------- Arrow enable/disable -------- */
  useEffect(() => {
    if (!emblaApi) return;
    const onUpdate = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    emblaApi.on('init', onUpdate);
    emblaApi.on('reInit', onUpdate);
    emblaApi.on('select', onUpdate);
    onUpdate();
    return () => {
      emblaApi.off('init', onUpdate);
      emblaApi.off('reInit', onUpdate);
      emblaApi.off('select', onUpdate);
    };
  }, [emblaApi]);

  /* ---------------- Width logic ---------------- */
  const containerRef = useRef(null);
  const [carouselWidth, setCarouselWidth] = useState('100vw');
  useEffect(() => {
    const calculateWidth = () => {
      if (!containerRef.current) return;
      const container = containerRef.current;
      const styles = window.getComputedStyle(container);
      const left = container.offsetLeft + parseFloat(styles.paddingLeft || '0');
      setCarouselWidth(`calc(100vw - ${left}px)`);
    };
    if (items.length) {
      calculateWidth();
      window.addEventListener('resize', calculateWidth);
    }
    return () => window.removeEventListener('resize', calculateWidth);
  }, [items.length]);
  useEffect(() => {
    const updateMaxWidth = () => {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      setMaxWidth(scrollbarWidth > 0 ? `calc(100vw - ${scrollbarWidth}px)` : '100vw');
    };
    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  /* ---------------- DOTS LOGIC ---------------- */
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  useEffect(() => {
    if (!emblaApi) return;
    const onInit = () => {
      setSlideCount(emblaApi.scrollSnapList().length);
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    emblaApi.on('init', onInit);
    emblaApi.on('reInit', onInit);
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('init', onInit);
      emblaApi.off('reInit', onInit);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);
  const MAX_DOTS = 5;
  const getVisibleDots = () => {
    if (slideCount <= MAX_DOTS) {
      return Array.from({
        length: slideCount
      }, (_, i) => i);
    }
    const half = Math.floor(MAX_DOTS / 2);
    if (selectedIndex <= half) {
      return [0, 1, 2, 3, 4];
    }
    if (selectedIndex >= slideCount - half - 1) {
      return Array.from({
        length: MAX_DOTS
      }, (__0, i_0) => slideCount - MAX_DOTS + i_0);
    }
    return Array.from({
      length: MAX_DOTS
    }, (__1, i_1) => selectedIndex - half + i_1);
  };

  /* ---------------- API ---------------- */
  const loadHeartHunters = async () => {
    try {
      const res = await fetch('/api/heart-hunters', {
        method: 'GET',
        cache: 'no-store'
      });
      if (!res.ok) {
        throw new Error('Failed to load heart hunters');
      }
      const json = await res.json();
      setItems(json.success ? json.data : []);
    } catch (err) {
      console.error(err);
      setItems([]);
    }
  };
  useEffect(() => {
    loadHeartHunters();
  }, []);
  if (!items.length) return null;
  return <section className="relative lg:pt-12 pt-10 pb-0 xl:-mb-70 md:-mb-60 -mb-52.5 z-[1] flex flex-col">
      <Image src={leftBg} alt="" className="absolute lg:top-[-20%] sm:top-[-10%] top-[-15%] pointer-events-none z-[-1] md:max-w-[22.5vw] sm:max-w-[30vw] max-w-[40vw]" aria-hidden="true" />
      <div className="container-wrap xl:pt-10 pt-0 lg:pb-0 pb-5">
        <div className="lg:max-w-[800px] max-w-[500px] mx-auto relative z-10 flex flex-col">
          {/* Heading */}
          <h2 className="text-[#323232] md:max-w-[100%] max-w-[70%] mx-auto font-semibold 2xl:text-[68px] xl:text-[54px] lg:text-[48px] sm:text-[36px] text-[26px] leading-[1em] text-center lg:mb-6">
            Join the Heart Hunters
          </h2>
        </div>
      </div>

      {/* DOTS */}
      <div className="flex lg:hidden justify-center gap-2 pb-2 lg:pb-0">
        {getVisibleDots().map(index => <button key={index} onClick={() => emblaApi?.scrollTo(index)} aria-label={`Go to slide ${index + 1}`} className={`h-1.5 rounded-full transition-all cursor-pointer duration-300
              ${index === selectedIndex ? 'w-4.25 bg-[#323232]' : 'w-1.5 bg-black/20'}`} />)}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-row container-wrap gap-4 items-center md:justify-center lg:order-2 order-3 xl:pb-13 lg:pb-10 lg:pt-0 sm:pt-2.5 sm:mt-0 -mt-8 relative z-[1]">
        <UIButton href="/leaderboard" variant="gradient" className="justify-center !Flex !font-semibold mx-auto !bg-white w-full sm:w-auto !max-w-[100%] sm:!max-w-max min-h-12">
          Join the Hunt
        </UIButton>
      </div>

      <div className={`relative overflow-hidden lg:order-3 order-2 ${styles.heartWrapper}`} style={{
      maxWidth
    }}>
        <div className="w-full text-[20px] max-w-[87.4em] mx-auto 2xl:px-[3.2em] xl:px-[2.5em] lg:px-[1.2em] px-0" ref={containerRef}>
          {/* Carousel */}
          <div ref={emblaRef} className={`overflow-hidden lg:pb-5 md:pb-4 pb-3 2xl:pt-20 xl:pt-15 pt-14 ${styles.heartCarousel}`} style={{
          width: carouselWidth
        }}>
            <div className="flex -ml-2 pr-2 sm:gap-0 gap-1">
              {items?.map(item => <div key={item.id} className="flex flex-[0_0_auto] md:px-2 px-1.5">
                  <HeartHunterCard data={item} />
                </div>)}
            </div>
          </div>
        </div>

        {/* Arrows */}
        <div className="hidden lg:flex">
          {hasScrolled && canPrev && <button onClick={scrollPrev} aria-label="Previous slide" className="2xl:w-14 2xl:h-28 w-12 h-24 bg-[#00EAFD] rounded-[0_10em_10em_0] cursor-pointer
            absolute left-0 top-1/2 -translate-y-1/2 flex items-center justify-center">
              <span className="sr-only">Previous</span>
              <IconArrow className="text-white w-6 rotate-180" />
            </button>}

          {canNext && <button onClick={scrollNext} aria-label="Next slide" className="2xl:w-14 2xl:h-28 w-12 h-24 bg-[#00EAFD] rounded-[10em_0_0_10em] cursor-pointer
            absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center">
              <span className="sr-only">Next</span>
              <IconArrow className="text-white w-6" />
            </button>}
        </div>
      </div>
    </section>;
}